import { ConstructionIcon } from "@faire/retailer-visitor-shared/features/Showroom/ShowroomVettingCriteria/ConstructionIcon";
import { FabricsIcon } from "@faire/retailer-visitor-shared/features/Showroom/ShowroomVettingCriteria/FabricsIcon";
import { PreciseFitIcon } from "@faire/retailer-visitor-shared/features/Showroom/ShowroomVettingCriteria/PreciseFitIcon";
import { StrictLocalizeFunction } from "@faire/web--source/common/localization";
import { TypographyVariants } from "@faire/web--source/slate/Typography/Types";
import React from "react";

export interface ISingleVettingCriteriaInfo {
  icon: React.ReactNode;
  vettingValue: string;
  vettingValueDescription?: string;
  color: string;
  titleTypographyVariants?: TypographyVariants;
  alignText?: string;
}

export enum VETTING_CRITERIA {
  QUALITY_FABRICS = "QUALITY_FABRICS",
  THOUGHTFUL_CONSTRUCTION = "THOUGHTFUL_CONSTRUCTION",
  PRECISE_FIT = "PRECISE_FIT",
}

export const showroomVettingCriteriaInfo = (
  color: string,
  shouldShowVettingValueDescription: boolean | undefined,
  titleTypographyVariants: TypographyVariants | undefined,
  strictLocalize: StrictLocalizeFunction
): Record<VETTING_CRITERIA, ISingleVettingCriteriaInfo> => ({
  [VETTING_CRITERIA.QUALITY_FABRICS]: {
    icon: <FabricsIcon color={color} />,
    vettingValue: strictLocalize({
      defaultMessage: "Quality fabrics",
      description: {
        text: "One of the vetting criteria for brands to be qualified as Showroom brands",
      },
    }),
    vettingValueDescription: shouldShowVettingValueDescription
      ? strictLocalize({
          defaultMessage:
            "We review fabric composition, weight, and texture, and look for the inclusion of natural fibers like cotton, silk, cashmere, and leather as good indicators of quality.",
          description: {
            text: "Description elaborating how we pick brands with Quality fabrics",
          },
        })
      : undefined,
    color,
    titleTypographyVariants,
  },
  [VETTING_CRITERIA.PRECISE_FIT]: {
    icon: <PreciseFitIcon color={color} />,
    vettingValue: strictLocalize({
      defaultMessage: "Precise fit",
      description: {
        text: "One of the vetting criteria for brands to be qualified as Showroom brands",
      },
    }),
    vettingValueDescription: shouldShowVettingValueDescription
      ? strictLocalize({
          defaultMessage:
            "Quality garments are tailored to fit the body and give it shape. We look at darts, seams, and topstitching, as well as how an item drapes to provide movement.",
          description: {
            text: "Description elaborating how we pick brands which are Precise fit",
          },
        })
      : undefined,
    color,
    titleTypographyVariants,
  },
  [VETTING_CRITERIA.THOUGHTFUL_CONSTRUCTION]: {
    icon: <ConstructionIcon color={color} />,
    vettingValue: strictLocalize({
      defaultMessage: "Thoughtful construction",
      description: {
        text: "One of the vetting criteria for brands to be qualified as Showroom brands",
      },
    }),
    vettingValueDescription: shouldShowVettingValueDescription
      ? strictLocalize({
          defaultMessage:
            "To ensure each item is designed and produced with care, we check everything from zippers and linings to transparent manufacturing methods.",
          description: {
            text: "Description elaborating how we pick brands with Thoughtful construction",
          },
        })
      : undefined,
    color,
    titleTypographyVariants,
  },
});
